import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, ArrowRight, Users, CheckSquare, Award, Check,
  Share2, Palette, Printer, MapPin, Mail, Send,
  Layers, Zap, Sparkles, MessageCircle, Trophy, TrendingUp,
  Rocket, Heart, Facebook, Instagram, Linkedin, Star, Quote,
  Building2, Home, UtensilsCrossed, ChevronLeft, ChevronRight, FileText, Headphones, X, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#0a1f44";
const NAVY_DARK = "#061530";
const ORANGE = "#e74d2d";

// ---------- Static content ----------
const STATS = [
  { icon: Users, value: "+150", label: "Clientes Felices" },
  { icon: CheckSquare, value: "+500", label: "Proyectos Completados" },
  { icon: Award, value: "100%", label: "Comprometidos" },
];

const SERVICE_CARDS = [
  {
    icon: Share2,
    iconBg: "bg-blue-600",
    checkBg: "bg-blue-600",
    cardBg: "bg-slate-50",
    title: "Social Media Management",
    description: "Administramos tus redes sociales para conectar con tu audiencia y hacer crecer tu marca.",
    items: ["Creación de contenido", "Diseño de publicaciones", "Calendario mensual", "Gestión de mensajes", "Reels y videos", "Reportes de resultados"],
  },
  {
    icon: Palette,
    iconBg: "bg-[#e74d2d]",
    checkBg: "bg-[#e74d2d]",
    cardBg: "bg-orange-50/70",
    title: "Diseño Gráfico",
    description: "Diseñamos la imagen que tu negocio necesita para destacar y transmitir profesionalismo.",
    items: ["Logos", "Branding", "Menús", "Flyers", "Brochures", "Business Cards", "Banners"],
  },
  {
    icon: Printer,
    iconBg: "bg-emerald-600",
    checkBg: "bg-emerald-600",
    cardBg: "bg-emerald-50/40",
    title: "Servicios de Impresión",
    description: "Impresión de alta calidad para materiales que dejan una impresión profesional y duradera.",
    items: ["Business Cards", "Flyers", "Brochures", "Door Hangers", "Postcards", "Menús", "Yard Signs", "Banners"],
  },
];

const FEATURES = [
  { icon: Layers, title: "Todo en un solo lugar", desc: "Marketing + Diseño + Impresión en un solo proveedor." },
  { icon: Zap, title: "Entrega Rápida", desc: "Producción y entrega eficiente para cumplir tus plazos." },
  { icon: Sparkles, title: "Diseños Profesionales", desc: "Contenido creativo que genera confianza y ventas." },
  { icon: MessageCircle, title: "Atención Personalizada", desc: "Cada negocio recibe una estrategia adaptada a sus objetivos." },
  { icon: Trophy, title: "Calidad Premium", desc: "Impresos con materiales de alta calidad y acabados profesionales." },
  { icon: TrendingUp, title: "Resultados Medibles", desc: "Estrategias enfocadas en crecimiento y retorno de inversión." },
];

const PORTFOLIO_TABS = ["TODOS", "DISEÑO GRÁFICO", "IMPRESIÓN", "REDES SOCIALES", "BRANDING"];

// Portfolio uses gradient + icon placeholders so user can swap images via Media Library later.
const PORTFOLIO_ITEMS = [
  { cat: "BRANDING",       label: "Luxor Real Estate",   gradient: "from-[#1a1a1a] to-[#3a2a1a]", icon: Building2 },
  { cat: "DISEÑO GRÁFICO", label: "Brochure Real Estate", gradient: "from-stone-800 to-stone-900", icon: Home },
  { cat: "DISEÑO GRÁFICO", label: "Menú Restaurant",     gradient: "from-amber-900 to-stone-900", icon: UtensilsCrossed },
  { cat: "REDES SOCIALES", label: "We Are Friends",      gradient: "from-rose-600 to-orange-500", icon: Heart },
  { cat: "IMPRESIÓN",      label: "Flyer Bilingüe",      gradient: "from-blue-900 to-indigo-900", icon: Printer },
  { cat: "IMPRESIÓN",      label: "Yard Sign · Open",    gradient: "from-amber-500 to-orange-700", icon: MapPin },
  { cat: "REDES SOCIALES", label: "Pizza · Social Post", gradient: "from-amber-700 to-red-800", icon: UtensilsCrossed },
  { cat: "BRANDING",       label: "Business Cards",      gradient: "from-stone-900 to-black", icon: Award },
  { cat: "REDES SOCIALES", label: "New Collection",      gradient: "from-stone-700 to-stone-900", icon: Sparkles },
  { cat: "DISEÑO GRÁFICO", label: "Menú Premium",        gradient: "from-stone-800 to-amber-900", icon: UtensilsCrossed },
  { cat: "REDES SOCIALES", label: "Thinking of Selling?", gradient: "from-sky-700 to-blue-900", icon: Home },
  { cat: "IMPRESIÓN",      label: "Door Hanger",         gradient: "from-amber-600 to-orange-800", icon: Printer },
];

const SOCIAL_PLATFORMS = [
  { icon: Facebook, color: "bg-[#1877F2]", name: "Facebook" },
  { icon: Instagram, color: "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600", name: "Instagram" },
  // TikTok: custom badge below since not in lucide as 'Tiktok'
  { custom: "TT", color: "bg-black", name: "TikTok" },
  { icon: Linkedin, color: "bg-[#0A66C2]", name: "LinkedIn" },
  { custom: "G", color: "bg-white text-stone-800 border", name: "Google" },
];

const PLANS = [
  {
    name: "STARTER", price: 299, popular: false,
    features: ["8 publicaciones", "Diseño gráfico", "Administración de Facebook", "Administración de Instagram", "Administración de página web"],
    btnClass: "bg-blue-600 hover:bg-blue-700",
  },
  {
    name: "GROWTH", price: 499, popular: true,
    features: ["12 publicaciones", "Diseño gráfico", "Reels", "Administración de página web", "Campañas promocionales"],
    btnClass: "bg-[#e74d2d] hover:bg-[#cf3d1f]",
  },
  {
    name: "PREMIUM", price: 899, popular: false,
    features: ["20 publicaciones", "Diseño gráfico", "Reels ilimitados", "Gestión de anuncios", "Administración de página web", "Reportes avanzados"],
    btnClass: "bg-blue-600 hover:bg-blue-700",
  },
];

const PRINT_PRODUCTS = [
  { label: "DOOR HANGERS",   gradient: "from-amber-600 to-orange-800", icon: Printer },
  { label: "BUSINESS CARDS", gradient: "from-amber-900 to-stone-900", icon: Award },
  { label: "FLYERS",         gradient: "from-amber-700 to-red-800",    icon: Sparkles },
  { label: "POSTCARDS",      gradient: "from-stone-800 to-stone-900",  icon: Mail },
  { label: "BROCHURES",      gradient: "from-stone-900 to-black",      icon: Layers },
  { label: "MENÚS",          gradient: "from-stone-700 to-stone-900",  icon: UtensilsCrossed },
];

const DEFAULT_TESTIMONIALS = [
  { name: "Restaurante Local", quote: "Sally Better transformó nuestra presencia digital y aumentó nuestros clientes.", icon: UtensilsCrossed },
  { name: "Realtor", quote: "Excelente calidad de impresión y diseños profesionales. Siempre entregan a tiempo.", icon: Home },
  { name: "Small Business Owner", quote: "Todo lo hacemos con ellos, desde redes sociales hasta tarjetas de presentación.", icon: Building2 },
];

const NAV_LINKS = [
  { key: "inicio", label: "INICIO", id: "inicio" },
  { key: "servicios", label: "SERVICIOS", id: "servicios" },
  { key: "portafolio", label: "PORTAFOLIO", id: "portafolio" },
  { key: "nosotros", label: "NOSOTROS", id: "nosotros" },
  { key: "contacto", label: "CONTACTO", id: "contacto" },
];

// ---------- Component ----------
const LandingPage = () => {
  const navigate = useNavigate();

  // Active nav section
  const [activeSection, setActiveSection] = useState("inicio");

  // Portfolio filter
  const [portfolioFilter, setPortfolioFilter] = useState("TODOS");

  // Approved surveys → testimonials
  const [approvedSurveys, setApprovedSurveys] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  // Contact dialog
  const [contactDialog, setContactDialog] = useState({ open: false, type: "", title: "" });
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", business: "", message: "", quantity: "" });
  const [isSendingContact, setIsSendingContact] = useState(false);

  // Newsletter
  const [newsletterEmail, setNewsletterEmail] = useState("");

  // Floating contact menu
  const [contactMenuOpen, setContactMenuOpen] = useState(false);

  // Editable landing settings (managed from admin panel)
  const [landingImages, setLandingImages] = useState({
    hero_image: "/assets/hero-mockup.jpg",
    print_image: "",
    portfolio_image: "",
  });
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [printItems, setPrintItems] = useState([]);
  const [callVisible, setCallVisible] = useState(true);
  const [callSchedule, setCallSchedule] = useState({ enabled: true, start: "08:00", end: "21:00", timezone: "America/New_York" });
  const [lightbox, setLightbox] = useState({ open: false, items: [], index: 0 });

  useEffect(() => {
    axios.get(`${API}/public/landing-settings`)
      .then(r => {
        const d = r.data || {};
        setLandingImages({
          hero_image: d.hero_image || "/assets/hero-mockup.jpg",
          print_image: d.print_image || "",
          portfolio_image: d.portfolio_image || "",
        });
        setPortfolioItems(Array.isArray(d.portfolio_items) ? d.portfolio_items : []);
        setPrintItems(Array.isArray(d.print_items) ? d.print_items : []);
        setCallVisible(d.call_visible !== false);
        if (d.call_schedule) setCallSchedule({ ...callSchedule, ...d.call_schedule });
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // Format schedule display (e.g., "8am-9pm ET")
  const formatScheduleTime = (timeStr) => {
    try {
      const [hStr, mStr] = (timeStr || "").split(":");
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (isNaN(h)) return timeStr;
      const period = h >= 12 ? "pm" : "am";
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const minPart = m && m !== 0 ? `:${String(m).padStart(2, "0")}` : "";
      return `${hour12}${minPart}${period}`;
    } catch {
      return timeStr;
    }
  };
  const scheduleLabel = `Disponible ${formatScheduleTime(callSchedule.start)}-${formatScheduleTime(callSchedule.end)} ET · WhatsApp 24/7`;

  // Load surveys → testimonials
  useEffect(() => {
    axios.get(`${API}/public/surveys/approved`)
      .then(r => setApprovedSurveys(r.data || []))
      .catch(() => { /* fallback to defaults */ });
  }, []);

  // Visitor engagement notification after 30s
  useEffect(() => {
    if (sessionStorage.getItem("landing_engagement_notified")) return;
    const t = setTimeout(() => {
      const info = {
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        referrer: document.referrer || "Direct",
        user_agent: navigator.userAgent,
        language: navigator.language,
        screen_size: `${window.screen.width}x${window.screen.height}`,
        engagement_seconds: 30,
      };
      axios.post(`${API}/public/visitor-engagement`, info)
        .then(() => sessionStorage.setItem("landing_engagement_notified", "true"))
        .catch(() => {});
    }, 30000);
    return () => clearTimeout(t);
  }, []);

  // Testimonials auto-rotate
  const testimonialsSource = approvedSurveys.length
    ? approvedSurveys.map(s => ({
        name: s.name || "Cliente",
        quote: s.improvement_suggestions || s.comments || "Excelente servicio.",
        rating: s.satisfaction_rating || 5,
        icon: Star,
      }))
    : DEFAULT_TESTIMONIALS;

  useEffect(() => {
    if (testimonialsSource.length <= 3) return;
    const i = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % testimonialsSource.length);
    }, 6000);
    return () => clearInterval(i);
  }, [testimonialsSource.length]);

  // Smooth scroll
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
      setActiveSection(id);
    }
  };

  // Scrollspy
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + 120;
      for (const link of [...NAV_LINKS].reverse()) {
        const el = document.getElementById(link.id);
        if (el && el.offsetTop <= y) {
          setActiveSection(link.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openContactDialog = (type, title) => {
    setContactDialog({ open: true, type, title });
  };

  const closeContactDialog = () => {
    setContactDialog({ open: false, type: "", title: "" });
    setContactForm({ name: "", email: "", phone: "", business: "", message: "", quantity: "" });
  };

  // Detect if current dialog is for a printable product that requires quantity
  const PRINTABLE_LABELS = ["DOOR HANGERS", "BUSINESS CARDS", "FLYERS", "POSTCARDS", "BROCHURES", "MENÚS", "MENUS"];
  const needsQuantity = () => {
    const t = (contactDialog.title || "").toUpperCase();
    return PRINTABLE_LABELS.some(l => t.includes(l));
  };
  const QUANTITY_OPTIONS = ["500", "1,000", "2,000", "2,000+"];

  // Resolve image URL: data: URLs / absolute URLs / public paths are used as-is.
  // Legacy relative server paths (e.g., /api/uploads/...) get the backend prefix.
  const resolveImg = (u) => {
    if (!u) return "";
    if (u.startsWith("data:") || u.startsWith("http") || u.startsWith("/assets/") || u.startsWith("blob:")) return u;
    if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    return `${process.env.REACT_APP_BACKEND_URL}/${u}`;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error("Por favor completa nombre y teléfono");
      return;
    }
    if (needsQuantity() && !contactForm.quantity) {
      toast.error("Por favor selecciona una cantidad");
      return;
    }
    setIsSendingContact(true);
    try {
      const subjectMap = {
        starter: "Plan Starter",
        growth: "Plan Growth",
        premium: "Plan Premium",
        quote: "Solicitud de Cotización",
        call: "Solicitud de Llamada",
        newsletter: "Suscripción Newsletter",
        general: "Contacto General",
      };
      const baseSubject = subjectMap[contactDialog.type] || "Contacto General";
      const subjectWithDetail = contactDialog.title ? `${baseSubject} · ${contactDialog.title}` : baseSubject;
      const qtyLine = contactForm.quantity ? `\nCantidad solicitada: ${contactForm.quantity}` : "";
      await axios.post(`${API}/contact`, {
        subject: subjectWithDetail,
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        business: contactForm.business,
        message: `${contactForm.message || ""}${qtyLine}`.trim(),
        plan_type: contactDialog.type,
      });
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      closeContactDialog();
    } catch {
      toast.error("Error al enviar el mensaje. Intenta de nuevo.");
    } finally {
      setIsSendingContact(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      await axios.post(`${API}/contact`, {
        subject: "Suscripción Newsletter",
        name: "Newsletter Subscriber",
        email: newsletterEmail,
        phone: "",
        business: "",
        message: "Quiero recibir tips y promociones",
        plan_type: "newsletter",
      });
      toast.success("¡Suscripción exitosa!");
      setNewsletterEmail("");
    } catch {
      toast.error("Error. Intenta de nuevo.");
    }
  };

  // Merge dynamic portfolio items with fallback gradient/icon from defaults
  const PORTFOLIO_ICONS_BY_CAT = {
    "BRANDING": Building2,
    "DISEÑO GRÁFICO": Home,
    "IMPRESIÓN": Printer,
    "REDES SOCIALES": Heart,
  };
  const mergedPortfolio = (portfolioItems.length ? portfolioItems : PORTFOLIO_ITEMS).map((p, idx) => {
    const fallback = PORTFOLIO_ITEMS[idx] || PORTFOLIO_ITEMS[0];
    return {
      id: p.id || `${idx}`,
      cat: p.category || p.cat || fallback.cat,
      label: p.label || fallback.label,
      gradient: p.gradient || fallback.gradient,
      icon: p.icon || PORTFOLIO_ICONS_BY_CAT[p.category || p.cat] || fallback.icon,
      image_url: p.image_url || "",
    };
  });
  const filteredPortfolio = portfolioFilter === "TODOS"
    ? mergedPortfolio
    : mergedPortfolio.filter(p => p.cat === portfolioFilter);

  const closeLightbox = () => setLightbox(l => ({ ...l, open: false }));
  const lightboxPrev = () => setLightbox(l => ({ ...l, index: (l.index - 1 + l.items.length) % l.items.length }));
  const lightboxNext = () => setLightbox(l => ({ ...l, index: (l.index + 1) % l.items.length }));

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") lightboxNext();
      else if (e.key === "ArrowLeft") lightboxPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open]);

  return (
    <div className="bg-stone-50 text-stone-900">
      {/* ============ HEADER ============ */}
      <header className="sticky top-0 z-50 backdrop-blur-md" style={{ backgroundColor: "#000000" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          {/* Logo */}
          <button onClick={() => scrollToSection("inicio")} className="flex items-center" data-testid="nav-logo">
            <img
              src="/assets/logo-sally-dark.png"
              alt="Sally Better — Print & Digital Solutions"
              className="h-14 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </button>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map(l => (
              <button
                key={l.key}
                onClick={() => scrollToSection(l.id)}
                className={`text-xs font-bold tracking-[0.15em] transition-colors ${
                  activeSection === l.id ? "text-[#e74d2d]" : "text-white/90 hover:text-[#e74d2d]"
                }`}
                data-testid={`nav-${l.key}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* CTAs */}
          <div className="flex items-center gap-2">
            {callVisible && (
              <Button
                asChild
                variant="ghost"
                className="hidden sm:inline-flex text-white hover:bg-white/10 hover:text-white font-bold px-4 py-5 rounded-full tracking-wider text-xs border border-white/30"
                data-testid="nav-llamar-ahora"
              >
                <a href="tel:+16172511444">
                  <Phone className="w-4 h-4 mr-1.5" /> LLAMAR AHORA
                </a>
              </Button>
            )}
            <Button
              onClick={() => openContactDialog("quote", "Solicitar Cotización")}
              className="bg-[#e74d2d] hover:bg-[#cf3d1f] text-white font-bold px-5 py-5 rounded-full tracking-wider text-xs shadow-lg"
              data-testid="nav-cotizar-ahora"
            >
              SOLICITAR COTIZACIÓN <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section id="inicio" className="relative overflow-hidden" style={{ backgroundColor: "#000000" }}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid lg:grid-cols-2 gap-8 items-center">
          {/* Copy - LEFT */}
          <div className="text-white relative z-10">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.05]">
              Haz Crecer<br />
              Tu Negocio con<br />
              <span className="text-[#e74d2d]">Marketing, Diseño<br />e Impresión Profesional</span>
            </h1>
            <p className="mt-6 text-white/80 text-base max-w-md leading-relaxed">
              Administramos tus redes sociales, diseñamos tu imagen corporativa y producimos todo el material impreso que tu negocio necesita.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => openContactDialog("quote", "Solicitar Cotización")}
                className="bg-[#e74d2d] hover:bg-[#cf3d1f] text-white font-bold px-7 py-6 rounded-full tracking-wider text-xs shadow-lg"
                data-testid="hero-solicitar-cotizacion"
              >
                SOLICITAR COTIZACIÓN <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {callVisible ? (
                <Button
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-stone-900 font-bold px-7 py-6 rounded-full tracking-wider text-xs"
                  data-testid="hero-llamar-ahora"
                >
                  <a href="tel:+16172511444">
                    <Phone className="w-4 h-4 mr-2" /> LLAMAR AHORA
                  </a>
                </Button>
              ) : (
                <a
                  href="https://wa.me/16172511444"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-schedule-badge"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/25 bg-white/5 backdrop-blur-sm text-white/85 text-xs font-semibold tracking-wide hover:bg-white/10 hover:text-white transition-colors"
                >
                  <Clock className="w-4 h-4 text-[#25D366]" />
                  <span>{scheduleLabel}</span>
                </a>
              )}
            </div>
            {/* Stats */}
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-6">
              {STATS.map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="flex items-center gap-3" data-testid={`hero-stat-${s.label}`}>
                    <div className="w-11 h-11 rounded-full border-2 border-[#e74d2d] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#e74d2d]" />
                    </div>
                    <div>
                      <p className="text-2xl font-extrabold text-white">{s.value}</p>
                      <p className="text-xs text-white/70">{s.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Image - RIGHT (desktop uses absolute bg, mobile uses inline) */}
          <div className="relative lg:hidden">
            <button
              type="button"
              onClick={() => openContactDialog("quote", "Solicitar Información")}
              aria-label="Solicitar información"
              data-testid="hero-image-mobile"
              className="block w-full focus:outline-none focus:ring-2 focus:ring-[#e74d2d] rounded-2xl"
            >
              <img
                src={landingImages.hero_image || "/assets/hero-mockup.jpg"}
                alt="Sally Better - Marketing, Diseño e Impresión"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </button>
          </div>
          {/* Spacer for desktop where image is absolute */}
          <div className="hidden lg:block" />
        </div>
        {/* Desktop: cropped mockup image as absolute background covering right half (clickable) */}
        <button
          type="button"
          onClick={() => openContactDialog("quote", "Solicitar Información")}
          aria-label="Solicitar información"
          data-testid="hero-image-desktop"
          className="absolute top-0 right-0 bottom-0 w-1/2 hidden lg:block cursor-pointer focus:outline-none"
          style={{
            backgroundImage: `url(${landingImages.hero_image || "/assets/hero-mockup.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      </section>

      {/* ============ IMPRESIÓN COMERCIAL (moved to "servicios" anchor) ============ */}
      <section id="servicios" className="py-16 lg:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-stone-900">IMPRESIÓN COMERCIAL DE ALTA CALIDAD</h2>
          <p className="mt-3 text-stone-600">Producción profesional para Realtors, Restaurantes, Contratistas y Negocios Locales.</p>

          {landingImages.print_image && (
            <div className="mt-10 rounded-2xl overflow-hidden shadow-lg max-h-[420px]">
              <img
                src={landingImages.print_image}
                alt="Impresión comercial destacada"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(printItems.length ? printItems : PRINT_PRODUCTS).map((p, idx) => {
              const fallback = PRINT_PRODUCTS[idx] || PRINT_PRODUCTS[0];
              const Icon = fallback.icon;
              const label = p.label || fallback.label;
              const img = p.image_url || "";
              return (
                <div
                  key={p.id || p.label}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow hover:shadow-xl hover:scale-[1.03] transition-all"
                  onClick={() => openContactDialog("quote", `Cotizar ${label}`)}
                  data-testid={`print-${label}`}
                >
                  {img ? (
                    <img
                      src={resolveImg(img)}
                      alt={label}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <>
                      <div className={`absolute inset-0 bg-gradient-to-br ${fallback.gradient}`} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Icon className="w-10 h-10 text-white/40" />
                      </div>
                    </>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-bold tracking-wider py-1.5 text-center">
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FEATURES BAR ============ */}
      <section className="py-12 bg-stone-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {FEATURES.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="text-center" data-testid={`feature-${f.title}`}>
                <div className="w-12 h-12 mx-auto rounded-xl bg-[#e74d2d]/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#e74d2d]" />
                </div>
                <h4 className="font-bold text-stone-900 text-sm">{f.title}</h4>
                <p className="text-xs text-stone-500 mt-1 leading-snug">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ PORTFOLIO ============ */}
      <section id="portafolio" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle>NUESTROS TRABAJOS</SectionTitle>

          {landingImages.portfolio_image && (
            <div className="mt-10 rounded-2xl overflow-hidden shadow-lg max-h-[420px]">
              <img
                src={landingImages.portfolio_image}
                alt="Portafolio destacado"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Filter pills */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {PORTFOLIO_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setPortfolioFilter(tab)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${
                  portfolioFilter === tab
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid={`portfolio-tab-${tab}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {filteredPortfolio.map((p, idx) => {
              const Icon = p.icon;
              const hasImage = !!p.image_url;
              const itemsWithImages = filteredPortfolio.filter(x => x.image_url);
              const imageIdx = itemsWithImages.findIndex(x => x.id === p.id);
              return (
                <button
                  type="button"
                  key={p.id || `${p.cat}-${p.label}-${idx}`}
                  onClick={() => {
                    if (hasImage) {
                      setLightbox({ open: true, items: itemsWithImages, index: imageIdx >= 0 ? imageIdx : 0 });
                    } else {
                      openContactDialog("quote", `Ver: ${p.label}`);
                    }
                  }}
                  className={`group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all focus:outline-none focus:ring-2 focus:ring-[#e74d2d] ${hasImage ? "" : `bg-gradient-to-br ${p.gradient}`}`}
                  data-testid={`portfolio-item-${p.label}`}
                  aria-label={`Ver ${p.label}`}
                >
                  {hasImage ? (
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.label}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon className="w-10 h-10 text-white/40" />
                    </div>
                  )}
                  <div className={`absolute inset-0 ${hasImage ? "bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100" : "bg-black/0 group-hover:bg-black/60"} transition-opacity flex items-end p-3`}>
                    <p className={`text-white text-xs font-semibold ${hasImage ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>{p.label}</p>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </section>

      {/* ============ SOCIAL MEDIA GROWTH ============ */}
      <section id="nosotros" className="py-16 lg:py-24 relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        {/* dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
          aria-hidden="true"
        />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#e74d2d]/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="text-white">
            <div className="flex items-center gap-3 mb-3">
              <Rocket className="w-8 h-8 text-[#e74d2d]" />
              <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight">Social Media Growth</h2>
            </div>
            <p className="text-lg lg:text-xl text-[#e74d2d] font-semibold mt-1">Convierte Seguidores en Clientes</p>
            <p className="mt-6 text-white/75 max-w-lg leading-relaxed text-[15px]">
              Administramos Facebook, Instagram, TikTok, LinkedIn y Google Business Profile para ayudar a tu negocio a generar más visibilidad, llamadas y ventas.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {["Creación de contenido", "Community Management", "Diseño gráfico profesional", "Campañas promocionales", "Reels y videos", "Reportes mensuales"].map(c => (
                <div key={c} className="flex items-center gap-3 text-[15px] text-white/95">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 shadow">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />
                  </span>
                  {c}
                </div>
              ))}
            </div>

            {/* Social platform pills */}
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              {SOCIAL_PLATFORMS.map(p => (
                <div
                  key={p.name}
                  className={`w-12 h-12 rounded-full ${p.color} flex items-center justify-center shadow-lg ring-1 ring-white/10`}
                  title={p.name}
                >
                  {p.custom ? (
                    <span className="font-bold text-base" style={{ color: p.color.includes("white") ? "#333" : "#fff" }}>{p.custom}</span>
                  ) : (
                    <p.icon className="w-6 h-6 text-white" />
                  )}
                </div>
              ))}
            </div>

            {/* CTA: VER PLANES */}
            <button
              onClick={() => scrollToSection("planes")}
              className="mt-10 inline-flex items-center gap-3 px-7 py-3.5 rounded-xl border-2 border-[#e74d2d] text-[#e74d2d] hover:bg-[#e74d2d] hover:text-white font-bold tracking-[0.18em] text-sm transition-colors group"
              data-testid="growth-ver-planes"
            >
              <FileText className="w-4 h-4" />
              VER PLANES
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Phone mockup image (real renders from mockup) */}
          <div className="relative">
            <img
              src="/assets/social-growth-phones.png?v=3"
              alt="Social Media Growth - vistas previas de redes sociales"
              className="w-full h-auto drop-shadow-2xl select-none"
              draggable="false"
            />
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle>LO QUE DICEN NUESTROS CLIENTES</SectionTitle>

          <Testimonials
            items={testimonialsSource}
            index={testimonialIndex}
            onIndex={setTestimonialIndex}
          />
        </div>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section id="contacto" className="py-12 lg:py-16" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="text-white text-center md:text-left">
            <h3 className="text-2xl lg:text-3xl font-extrabold">¿Listo para Llevar tu Negocio al Siguiente Nivel?</h3>
            <p className="text-white/70 mt-1">Obtén una consulta gratuita hoy mismo.</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => openContactDialog("quote", "Solicitar Cotización")}
              className="bg-[#e74d2d] hover:bg-[#cf3d1f] text-white font-bold px-6 py-6 rounded-md tracking-wider text-xs"
              data-testid="cta-cotizar"
            >
              SOLICITAR COTIZACIÓN <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {callVisible ? (
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white/80 text-white hover:bg-white hover:text-stone-900 font-bold px-6 py-6 rounded-md tracking-wider text-xs"
                data-testid="cta-llamar"
              >
                <a href="tel:+16172511444">
                  <Phone className="w-4 h-4 mr-2" /> LLAMAR AHORA
                </a>
              </Button>
            ) : (
              <a
                href="https://wa.me/16172511444"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-schedule-badge"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/30 bg-white/5 backdrop-blur-sm text-white/85 text-xs font-semibold tracking-wide hover:bg-white/10 hover:text-white transition-colors"
              >
                <Clock className="w-4 h-4 text-[#25D366]" />
                <span>{scheduleLabel}</span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer style={{ backgroundColor: NAVY_DARK }} className="py-12 text-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-1">
            <img src="/assets/logo-sally-light.jpg" alt="Sally Better" className="h-12 mb-3 rounded" />
            <p className="text-sm text-white/60 leading-relaxed">Marketing Digital · Diseño Gráfico · Impresión Comercial</p>
            <div className="mt-4 flex gap-3">
              <a href="https://www.facebook.com/GrupoAURArd/" target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#e74d2d] transition-colors"><Facebook className="w-5 h-5" /></a>
              <a href="https://www.instagram.com/sallybetter_" target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#e74d2d] transition-colors"><Instagram className="w-5 h-5" /></a>
            </div>
          </div>

          <div>
            <p className="font-bold text-white mb-3">SERVICIOS</p>
            <ul className="space-y-1.5 text-sm">
              <li><button onClick={() => scrollToSection("servicios")} className="hover:text-[#e74d2d]">Marketing Digital</button></li>
              <li><button onClick={() => scrollToSection("servicios")} className="hover:text-[#e74d2d]">Diseño Gráfico</button></li>
              <li><button onClick={() => scrollToSection("servicios")} className="hover:text-[#e74d2d]">Impresión Comercial</button></li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-white mb-3">ENLACES RÁPIDOS</p>
            <ul className="space-y-1.5 text-sm">
              {NAV_LINKS.map(l => (
                <li key={l.key}><button onClick={() => scrollToSection(l.id)} className="hover:text-[#e74d2d] capitalize">{l.label.toLowerCase()}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-bold text-white mb-3">CONTÁCTANOS</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> <a href="tel:+16172511444" className="hover:text-[#e74d2d]">(617) 251-1444</a></li>
              <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> <a href="mailto:hello@sallybetter.com" className="hover:text-[#e74d2d]">hello@sallybetter.com</a></li>
              <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Boston, Massachusetts</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-white mb-3">SUSCRÍBETE</p>
            <p className="text-xs text-white/60 mb-3">Recibe tips y promociones exclusivas para tu negocio.</p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-1">
              <Input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Tu correo electrónico"
                className="bg-white/10 border-white/20 text-white text-xs placeholder:text-white/40"
                data-testid="newsletter-email"
              />
              <Button type="submit" className="bg-[#e74d2d] hover:bg-[#cf3d1f] px-3 shrink-0" data-testid="newsletter-submit">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-xs text-white/40 gap-2">
          <p>© {new Date().getFullYear()} Sally Better. Todos los derechos reservados.</p>
          <button
            onClick={() => navigate("/login")}
            className="hover:text-white/80"
            data-testid="footer-admin-login"
          >
            Admin Login
          </button>
        </div>
      </footer>

      {/* ============ CONTACT DIALOG ============ */}
      <Dialog open={contactDialog.open} onOpenChange={(o) => !o && closeContactDialog()}>
        <DialogContent className="max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>{contactDialog.title || "Contáctanos"}</DialogTitle>
            <DialogDescription>Te responderemos en menos de 24 horas.</DialogDescription>
          </DialogHeader>

          {/* Quick contact - WhatsApp shortcut */}
          <a
            href={`https://wa.me/16172511444?text=${encodeURIComponent("Hola Sally Better, me interesa: " + (contactDialog.title || "más información"))}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-dialog-whatsapp"
            className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 border-2 border-[#25D366]/30 hover:border-[#25D366] transition-colors group"
          >
            <div className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center shadow shrink-0">
              <svg viewBox="0 0 32 32" className="w-5 h-5 fill-white" aria-hidden="true">
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.79 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.495-1.318.144-.33.215-.717.215-1.075 0-.114 0-.114-.143-.187-.244-.143-2.354-1.347-2.59-1.347zm-2.79 9.83a11.058 11.058 0 0 1-5.668-1.563l-.388-.23-4.07.86.882-3.964-.243-.41C5.71 19.51 5 17.347 5 15.066 5 9.4 9.4 5 15.075 5c2.764 0 5.357 1.072 7.314 3.028a10.252 10.252 0 0 1 3.027 7.32c0 5.66-4.4 10.063-10.067 10.063h.1zm9.5-17.585A14.43 14.43 0 0 0 16.4 3c-9.5 0-14 8.5-14 14 0 2.8.7 5.5 2.1 7.9L2 31l6.3-1.6c2.3 1.3 4.9 1.9 7.5 1.9 9.4 0 14-8.5 14-14 0-2.7-.5-5.5-2-8z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold text-stone-900 text-sm">Hablar con un agente ahora</p>
              <p className="text-xs text-stone-600">Respuesta inmediata por WhatsApp</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#25D366] group-hover:translate-x-1 transition-transform" />
          </a>

          {/* Divider */}
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-[11px] font-semibold text-stone-400 tracking-wider">O LLENA EL FORMULARIO</span></div>
          </div>

          <form onSubmit={handleContactSubmit} className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cf-name">Nombre *</Label>
              <Input id="cf-name" value={contactForm.name} onChange={(e) => setContactForm(p => ({ ...p, name: e.target.value }))} required data-testid="contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-email">Email</Label>
              <Input id="cf-email" type="email" value={contactForm.email} onChange={(e) => setContactForm(p => ({ ...p, email: e.target.value }))} data-testid="contact-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cf-phone">Teléfono *</Label>
                <Input id="cf-phone" value={contactForm.phone} onChange={(e) => setContactForm(p => ({ ...p, phone: e.target.value }))} required data-testid="contact-phone" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-business">Negocio</Label>
                <Input id="cf-business" value={contactForm.business} onChange={(e) => setContactForm(p => ({ ...p, business: e.target.value }))} data-testid="contact-business" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-message">Mensaje</Label>
              <Textarea id="cf-message" rows={3} value={contactForm.message} onChange={(e) => setContactForm(p => ({ ...p, message: e.target.value }))} placeholder="Cuéntanos sobre tu proyecto..." data-testid="contact-message" />
            </div>
            {needsQuantity() && (
              <div className="space-y-1.5" data-testid="contact-quantity-field">
                <Label htmlFor="cf-quantity">Cantidad <span className="text-[#e74d2d]">*</span></Label>
                <div className="grid grid-cols-4 gap-2">
                  {QUANTITY_OPTIONS.map(opt => (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setContactForm(p => ({ ...p, quantity: opt }))}
                      data-testid={`contact-quantity-${opt}`}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-bold transition-all ${
                        contactForm.quantity === opt
                          ? "border-[#e74d2d] bg-[#e74d2d]/10 text-[#e74d2d]"
                          : "border-stone-200 text-stone-700 hover:border-stone-400"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {contactForm.quantity === "2,000+" && (
                  <p className="text-xs text-stone-500 mt-1">Coordinaremos cantidad exacta y descuento por volumen.</p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeContactDialog}>Cancelar</Button>
              <Button type="submit" disabled={isSendingContact} className="bg-[#e74d2d] hover:bg-[#cf3d1f]" data-testid="contact-submit">
                {isSendingContact ? "Enviando..." : "Enviar"}
                {!isSendingContact && <Send className="w-4 h-4 ml-1" />}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ LIGHTBOX (portfolio image viewer) ============ */}
      {lightbox.open && lightbox.items.length > 0 && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Visor de imagen"
          data-testid="lightbox"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Cerrar"
            data-testid="lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev */}
          {lightbox.items.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              aria-label="Anterior"
              data-testid="lightbox-prev"
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Image content */}
          <div className="relative max-w-5xl max-h-[88vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveImg(lightbox.items[lightbox.index].image_url)}
              alt={lightbox.items[lightbox.index].label}
              className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl"
              data-testid="lightbox-image"
            />
            {/* Caption */}
            <div className="mt-4 px-4 text-center text-white">
              <p className="text-[10px] tracking-widest text-[#e74d2d] font-bold">{lightbox.items[lightbox.index].cat}</p>
              <p className="mt-1 text-base font-bold">{lightbox.items[lightbox.index].label}</p>
              {lightbox.items.length > 1 && (
                <p className="text-xs text-white/60 mt-1">{lightbox.index + 1} / {lightbox.items.length}</p>
              )}
            </div>
          </div>

          {/* Next */}
          {lightbox.items.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              aria-label="Siguiente"
              data-testid="lightbox-next"
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>
      )}

      {/* ============ FLOATING MULTI-CHANNEL CONTACT FAB ============ */}
      <div className="fixed right-5 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-center gap-3" data-testid="floating-contact-fab">
        {/* Expanding channel buttons - shown when open */}
        <div
          className={`flex flex-col items-center gap-3 transition-all duration-300 ${contactMenuOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"}`}
        >
          {/* WhatsApp */}
          <a
            href="https://wa.me/16172511444"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chatear por WhatsApp"
            data-testid="fab-whatsapp"
            className="group relative w-12 h-12 rounded-full bg-[#25D366] hover:scale-110 transition-transform shadow-lg ring-2 ring-white/40 flex items-center justify-center text-white"
          >
            <svg viewBox="0 0 32 32" className="w-6 h-6 fill-current" aria-hidden="true">
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.79 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.495-1.318.144-.33.215-.717.215-1.075 0-.114 0-.114-.143-.187-.244-.143-2.354-1.347-2.59-1.347zm-2.79 9.83a11.058 11.058 0 0 1-5.668-1.563l-.388-.23-4.07.86.882-3.964-.243-.41C5.71 19.51 5 17.347 5 15.066 5 9.4 9.4 5 15.075 5c2.764 0 5.357 1.072 7.314 3.028a10.252 10.252 0 0 1 3.027 7.32c0 5.66-4.4 10.063-10.067 10.063h.1zm9.5-17.585A14.43 14.43 0 0 0 16.4 3c-9.5 0-14 8.5-14 14 0 2.8.7 5.5 2.1 7.9L2 31l6.3-1.6c2.3 1.3 4.9 1.9 7.5 1.9 9.4 0 14-8.5 14-14 0-2.7-.5-5.5-2-8z"/>
            </svg>
            <span className="absolute right-full mr-3 px-2 py-1 bg-stone-900 text-white text-[11px] font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">WhatsApp</span>
          </a>
          {/* Instagram */}
          <a
            href="https://www.instagram.com/sallybetter_"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Síguenos en Instagram"
            data-testid="fab-instagram"
            className="group relative w-12 h-12 rounded-full hover:scale-110 transition-transform shadow-lg ring-2 ring-white/40 flex items-center justify-center text-white"
            style={{ background: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
          >
            <Instagram className="w-6 h-6" />
            <span className="absolute right-full mr-3 px-2 py-1 bg-stone-900 text-white text-[11px] font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Instagram</span>
          </a>
          {/* Facebook */}
          <a
            href="https://www.facebook.com/GrupoAURArd/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Síguenos en Facebook"
            data-testid="fab-facebook"
            className="group relative w-12 h-12 rounded-full bg-[#1877F2] hover:scale-110 transition-transform shadow-lg ring-2 ring-white/40 flex items-center justify-center text-white"
          >
            <Facebook className="w-6 h-6 fill-current" />
            <span className="absolute right-full mr-3 px-2 py-1 bg-stone-900 text-white text-[11px] font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Facebook</span>
          </a>
          {/* SMS */}
          <a
            href="sms:+16172511444"
            aria-label="Enviar SMS"
            data-testid="fab-sms"
            className="group relative w-12 h-12 rounded-full bg-pink-500 hover:scale-110 transition-transform shadow-lg ring-2 ring-white/40 flex items-center justify-center text-white"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute right-full mr-3 px-2 py-1 bg-stone-900 text-white text-[11px] font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Mensaje de Texto</span>
          </a>
          {/* Email */}
          <a
            href="mailto:hello@sallybetter.com"
            aria-label="Enviar correo electrónico"
            data-testid="fab-email"
            className="group relative w-12 h-12 rounded-full bg-blue-600 hover:scale-110 transition-transform shadow-lg ring-2 ring-white/40 flex items-center justify-center text-white"
          >
            <Mail className="w-6 h-6" />
            <span className="absolute right-full mr-3 px-2 py-1 bg-stone-900 text-white text-[11px] font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">Correo Electrónico</span>
          </a>
        </div>

        {/* Toggle button: agent / close */}
        <button
          type="button"
          onClick={() => setContactMenuOpen(o => !o)}
          aria-label={contactMenuOpen ? "Cerrar opciones de contacto" : "Hablar con un agente"}
          aria-expanded={contactMenuOpen}
          data-testid="fab-toggle"
          className={`relative w-14 h-14 rounded-full text-white shadow-2xl ring-4 ring-white/30 flex items-center justify-center transition-all hover:scale-110 ${contactMenuOpen ? "bg-stone-800" : "bg-[#e74d2d]"}`}
        >
          {!contactMenuOpen && (
            <span className="absolute inset-0 rounded-full bg-[#e74d2d] opacity-60 animate-ping" aria-hidden="true" />
          )}
          <span className="relative">
            {contactMenuOpen ? <X className="w-6 h-6" /> : <Headphones className="w-6 h-6" />}
          </span>
        </button>
      </div>
    </div>
  );
};

// ---------- Small subcomponents ----------
const SectionTitle = ({ children }) => (
  <div className="text-center">
    <h2 className="text-3xl lg:text-4xl font-extrabold text-stone-900 tracking-wide">{children}</h2>
    <div className="mt-3 w-16 h-1 bg-[#e74d2d] mx-auto rounded-full" />
  </div>
);

const HeroMockup = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    {/* Laptop */}
    <div className="absolute top-4 left-8 w-72 h-44 rounded-md bg-gradient-to-br from-stone-700 to-stone-800 shadow-2xl border-4 border-stone-900 overflow-hidden hidden md:block">
      <div className="absolute inset-1 rounded-sm bg-gradient-to-br from-amber-100 to-amber-200">
        <div className="h-4 bg-amber-700/80" />
        <div className="p-2 grid grid-cols-3 gap-1">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-video bg-amber-900/40 rounded" />
          ))}
        </div>
      </div>
    </div>
    {/* Phone */}
    <div className="absolute top-12 left-2 w-24 h-44 rounded-2xl bg-stone-900 shadow-2xl border-4 border-stone-800 overflow-hidden hidden sm:block">
      <div className="absolute inset-1 rounded-xl bg-gradient-to-b from-rose-400 to-pink-500">
        <div className="p-2 space-y-1.5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-6 bg-white/20 rounded" />
          ))}
        </div>
      </div>
    </div>
    {/* Door hanger */}
    <div className="absolute top-0 right-12 w-32 h-56 rounded-xl bg-gradient-to-br from-amber-600 to-orange-700 shadow-2xl hidden md:flex flex-col items-center justify-center text-white">
      <div className="w-8 h-8 rounded-full border-4 border-amber-800 mb-1" />
      <p className="text-[10px] font-bold tracking-wider">OPEN HOUSE</p>
      <p className="text-[8px] mt-1 opacity-80">THIS WEEKEND</p>
      <p className="text-[10px] font-bold mt-1">(857) 123-4567</p>
    </div>
    {/* Just listed sign */}
    <div className="absolute bottom-6 right-0 w-40 h-52 rounded-md bg-white shadow-2xl border border-stone-200 hidden md:flex flex-col items-center justify-center p-2">
      <p className="text-stone-900 text-[10px] font-bold tracking-wider">JUST LISTED</p>
      <p className="text-amber-700 text-[8px]">Beautiful Home</p>
      <div className="mt-1 w-full h-20 rounded bg-gradient-to-br from-amber-200 to-orange-300" />
      <p className="text-stone-600 text-[7px] mt-1">SB Real Estate</p>
    </div>
    {/* Menu */}
    <div className="absolute bottom-0 left-16 w-44 h-32 rounded bg-gradient-to-br from-stone-800 to-stone-900 shadow-2xl flex flex-col items-center justify-center text-white border border-amber-700/40 hidden md:flex">
      <p className="text-[10px] font-bold text-amber-500 tracking-wider">GRAND OPENING</p>
      <div className="mt-1 w-32 h-16 rounded bg-amber-700/30" />
    </div>
  </div>
);

const Testimonials = ({ items, index, onIndex }) => {
  if (!items || items.length === 0) return null;
  // Show 3 at a time
  const visible = items.length <= 3
    ? items
    : [0, 1, 2].map(o => items[(index + o) % items.length]);
  const prev = () => onIndex((index - 1 + items.length) % items.length);
  const next = () => onIndex((index + 1) % items.length);

  return (
    <div className="mt-12">
      <div className="grid md:grid-cols-3 gap-5">
        {visible.map((t, i) => {
          const Icon = t.icon || Star;
          return (
            <div key={`${t.name}-${i}`} className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-200 to-orange-300 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <Quote className="w-5 h-5 text-stone-300 mb-2" />
              <p className="text-sm text-stone-700 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <p className="mt-3 text-xs text-stone-500 font-medium">— {t.name}</p>
            </div>
          );
        })}
      </div>

      {items.length > 3 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={prev} className="w-9 h-9 rounded-full border bg-white hover:bg-stone-50 flex items-center justify-center" data-testid="testimonial-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => onIndex(i)}
                className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-[#e74d2d]" : "w-2 bg-stone-300"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button onClick={next} className="w-9 h-9 rounded-full border bg-white hover:bg-stone-50 flex items-center justify-center" data-testid="testimonial-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
